import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, viewChild, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';

import { first } from 'lodash-es';
import { BehaviorSubject, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

import { ErrorComponent } from '../../../shared/components/error/error.component';
import { FileUploaderComponent } from '../../../shared/components/file-uploader/file-uploader.component';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { WarningComponent } from '../../../shared/components/warning/warning.component';
import { MimeType } from '../../../shared/models/common/mime-type.enum';
import { FileUploadResult } from '../../../shared/services/file/file.service';
import { ButtonDirective } from '../../../ui/button/button/button.directive';
import { IconButtonDirective } from '../../../ui/button/icon-button/icon-button.directive';
import { UploadedDocument } from '../../models/uploaded-document.model';
import { TransactionDocumentService } from '../../services/transaction-document.service';

export interface UploadDocumentAdminModalData {
  document: UploadedDocument;
}

@Component({
  selector: 'semeris-upload-document-admin-modal',
  imports: [
    CommonModule,
    MatStepperModule,
    MatIconModule,
    LoaderComponent,
    FileUploaderComponent,
    WarningComponent,
    ErrorComponent,
    IconButtonDirective,
    MatDialogModule,
    ButtonDirective,
  ],
  templateUrl: './upload-document-admin-modal.component.html',
  styleUrls: ['./upload-document-admin-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadDocumentAdminModalComponent {
  data = inject<UploadDocumentAdminModalData>(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<UploadDocumentAdminModalComponent>>(MatDialogRef);
  private readonly transactionDocumentService = inject(TransactionDocumentService);

  public error$ = new BehaviorSubject<string>('');
  public file$ = new BehaviorSubject<File>(null);
  public loading$ = new BehaviorSubject<boolean>(false);

  public readonly allowedMimeTypes = [MimeType.Docx.toString()];
  public readonly accept = 'docx';

  public readonly stepperComponent = viewChild(MatStepper);

  public onFilesSelected(files: File[]) {
    this.error$.next('');
    this.file$.next(first(files));
    this.stepperComponent().selected.completed = true;
    this.stepperComponent().next();
  }

  public onConfirm() {
    this.loading$.next(true);

    this.transactionDocumentService
      .uploadFile({
        assetShortName: this.data.document.assetShortName,
        libraryShortName: this.data.document.libraryShortName,
        originalFilename: {
          DOCX: this.file$.value.name,
        },
        uuid: this.data.document.uuid,
        file: this.file$.value,
      })
      .pipe(
        filter(({ uploadStatus }) => [FileUploadResult.COMPLETED, FileUploadResult.FAILED].includes(uploadStatus)),
        take(1),
        switchMap(({ uploadStatus }) =>
          uploadStatus === FileUploadResult.COMPLETED
            ? this.transactionDocumentService.triggerManualDocxUpload(this.data.document.uuid)
            : of(false)
        )
      )
      .subscribe((succeeded) => {
        if (succeeded) {
          this.stepperComponent().selected.completed = true;
          this.stepperComponent().next();
        } else {
          this.stepperComponent().selected.completed = false;
          this.stepperComponent().previous();
          this.error$.next('Failed to trigger manual docx upload.');
        }
      });
  }

  public onCancel() {
    this.stepperComponent().selected.completed = false;
    this.stepperComponent().previous();
  }

  public onClose() {
    this.dialogRef.close(true);
  }

  public onInvalidFilesSelected(files: File[]) {
    this.error$.next(first(files)?.name ? `Invalid file: ${first(files).name}. You can only upload docx files.` : '');
  }
}
